/**
 * The five microphone pages (SEO_BRIEF_08 section 3, tools/mic.html): the microphone panel
 * (lib/mic-panel.ts) and B6's upload behind its Transcribe button. /live-captions (SEO_BRIEF_09
 * section 2.5) is the same template with the live captions view (lib/live-captions.ts) in the
 * panel's place, and no upload.
 *
 * A recording goes exactly where an audio file dropped on /audio-to-text goes: the same upload
 * (lib/upload-offer.ts `send`, without the banner, because Transcribe is the visitor's yes), the
 * same cap and refusals in the banner region, the same job page, and the transcript page with the
 * page's own format first (`?download=`, lib/download-pref.ts).
 */

import { must, qs } from '../lib/dom.ts';
import { withDownload } from '../lib/download-pref.ts';
import { countCaptionsStart, mountLiveCaptions } from '../lib/live-captions.ts';
import { mountMicPanel } from '../lib/mic-panel.ts';
import { loadQuota } from '../lib/quota.ts';
import { translateTarget, withAsked } from '../lib/translate-target.ts';
import { createUploadOffer } from '../lib/upload-offer.ts';
import { noteResult } from '../lib/usage.ts';

/** The page's transcript download, from the config block tools/mic.html writes. */
function transcriptFormat(): string | null {
  const block = qs<HTMLScriptElement>('script[data-tool-config]');
  try {
    const config = JSON.parse(block?.textContent ?? 'null') as { transcript_format?: unknown };
    return typeof config?.transcript_format === 'string' ? config.transcript_format : null;
  } catch {
    return null;
  }
}

export function mountMicPage(): void {
  const page = qs<HTMLElement>('[data-tool-page]');
  const captions = page ? qs<HTMLElement>('[data-captions]', page) : null;
  if (captions) {
    const tool = captions.dataset['usageTool'] ?? '';
    mountLiveCaptions(captions, {
      onStart: (lang) => countCaptionsStart(tool, lang),
      // A save has no beacon; the rating card hears it all the same (lib/rating-prompt.ts).
      onSave: () => noteResult(tool, 'download'),
    });
    return;
  }
  const panel = page ? qs<HTMLElement>('[data-mic-panel]', page) : null;
  if (!page || !panel) return;
  const format = transcriptFormat();
  const uploads = createUploadOffer({
    region: must<HTMLElement>('#banner-region'),
    tool: page.dataset['toolSlug'] ?? '',
    onSubmitted: (response) => {
      const next = response.resolved
        ? withAsked(response.transcript_url, response.translate)
        : response.job_url;
      location.assign(withDownload(next, format));
    },
    // /voice-translator-to-english (SEO_BRIEF_09 5.1): the recording is translated into the
    // picker's language. Every other microphone page has no picker and sends none.
    translateTo: () => translateTarget(),
  });
  mountMicPanel(panel, {
    transcribe: (file, seconds, counted) => uploads.send(file, seconds, counted),
    loadQuota: () => void loadQuota(),
  });
}
